import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { useNavigate } from "react-router-dom";
import Sidebar from "../AdminPanel/Sidebar";
import TopBar from "../AdminPanel/TopBar";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import ProductForm from "./Products/ProductForm";
import ProductImages from "./Products/ProductImages";

export default function Products() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const navigate = useNavigate();

  const [nextCode, setNextCode] = useState("Cargando...");
  const [form, setForm] = useState({
    name: "",
    ecommerce: true,
    stock_minimo: "",
    pvp1: "",
    iva_porcentaje: 15,
    category: null,
    brand: null,
    barcode: "",
    codigo_auxiliar: "",
    estado: "activo",
    description: "",
  });

  const [images, setImages] = useState([]);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "loading",
    message: "",
  });

  const token = localStorage.getItem("accessToken");

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const loadNextCode = async () => {
    try {
      const response = await fetch("/api/products/products/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setNextCode("Se asignará automáticamente");
        return;
      }

      const results = data.results || [];
      const maxSeq = results.reduce((max, item) => {
        const seq = Number(item.product_seq || 0);
        return seq > max ? seq : max;
      }, 0);

      const code = `P${String(maxSeq + 1).padStart(7, "0")}`;
      setNextCode(code);
    } catch {
      setNextCode("Se asignará automáticamente");
    }
  };

  useEffect(() => {
    loadNextCode();
  }, []);

  const showErrorModal = (message) => {
    setStatusModal({
      open: true,
      type: "error",
      message,
    });
  };

  const closeStatusModal = () => {
    if (statusModal.type === "loading") return;
    setStatusModal((prev) => ({ ...prev, open: false }));
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      showErrorModal("El nombre del producto es obligatorio.");
      return false;
    }

    if (form.pvp1 === "" || Number(form.pvp1) < 0) {
      showErrorModal("El PVP debe ser mayor o igual a cero.");
      return false;
    }

    if (form.stock_minimo !== "" && Number(form.stock_minimo) < 0) {
      showErrorModal("El stock mínimo no puede ser negativo.");
      return false;
    }

    return true;
  };

  const parseBackendError = (data) => {
    const error = data?.error || data?.detail;

    if (!error) {
      return "No se pudo crear el producto.";
    }

    if (typeof error === "string") {
      return error;
    }

    if (typeof error === "object") {
      return Object.values(error).flat().join(" ");
    }

    return "No se pudo crear el producto.";
  };

  const saveProduct = async () => {
    if (!validateForm()) return;

    try {
      setStatusModal({
        open: true,
        type: "loading",
        message: "Guardando producto...",
      });

      const fd = new FormData();

      fd.append("name", form.name.trim());
      fd.append("ecommerce", form.ecommerce ? "true" : "false");
      fd.append("stock_minimo", form.stock_minimo === "" ? "" : form.stock_minimo);
      fd.append("pvp1", form.pvp1);
      fd.append("iva_porcentaje", form.iva_porcentaje);
      fd.append("aplica_ice", "false");
      fd.append("ice_porcentaje", "0");
      fd.append("category_id", form.category?.id || "");
      fd.append("brand_id", form.brand?.id || "");
      fd.append("barcode", form.barcode.trim());
      fd.append("codigo_auxiliar", form.codigo_auxiliar.trim());
      fd.append("estado", form.estado);
      fd.append("description", form.description.trim());

      images.forEach((item) => {
        fd.append("images", item.file);
      });

      const response = await fetch("/api/products/products/create/", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await response.json();

      if (!response.ok) {
        setStatusModal({
          open: true,
          type: "error",
          message: parseBackendError(data),
        });
        return;
      }

      setStatusModal({
        open: true,
        type: "success",
        message: data.mensaje || "Producto añadido exitosamente.",
      });

      setTimeout(() => {
        navigate(-1);
      }, 3000);
    } catch {
      setStatusModal({
        open: true,
        type: "error",
        message: "Error de conexión.",
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 24, sm: 26, md: 30 },
                  fontWeight: 600,
                  color: dashboardTheme.primary_color,
                  lineHeight: 1.2,
                }}
              >
                Nuevo Producto
              </Typography>

              <Button
                variant="outlined"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate(-1)}
                sx={outlinedButtonSx}
              >
                Regresar
              </Button>
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: "10px", md: "12px" },
                border: `1px solid ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.card_background,
                p: { xs: 1.5, sm: 2, md: 2.5 },
              }}
            >
              <Stack spacing={3}>
                <ProductForm
                  form={form}
                  setForm={setForm}
                  nextCode={nextCode}
                  onSubmit={saveProduct}
                />

                <ProductImages images={images} setImages={setImages} />
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={statusModal.open}
        onClose={closeStatusModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogContent
          sx={{
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
          }}
        >
          {statusModal.type === "loading" && (
            <CircularProgress size={42} sx={{ color: dashboardTheme.primary_color }} />
          )}

          {statusModal.type === "success" && (
            <CheckCircleRoundedIcon sx={{ fontSize: 48, color: "#2E7D32" }} />
          )}

          {statusModal.type === "error" && (
            <ErrorOutlineRoundedIcon sx={{ fontSize: 48, color: "#D32F2F" }} />
          )}

          <Typography
            sx={{
              color:
                statusModal.type === "error"
                  ? "#D32F2F"
                  : statusModal.type === "success"
                  ? "#2E7D32"
                  : dashboardTheme.text_color,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {statusModal.message}
          </Typography>

          {statusModal.type === "error" && (
            <Button
              variant="contained"
              onClick={closeStatusModal}
              sx={dashboardTheme.buttonStyles.contained}
            >
              Entendido
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}